import React from 'react'
import { Lineup, LineupRubric } from './store/types'
import { Button, Divider, List, ListItem, Modal, Stack } from '@mui/material'
import { AddCircle, Close } from '@material-ui/icons'
import { LockOutlined, LockOpenOutlined } from '@mui/icons-material'
import PlayerModal from './PlayerModal'
import { useDispatch, useSelector } from 'react-redux'
import { removePlayer, setProjectionsRedux, setSelectedLineup, setSlateProjections, togglePlayerLock } from './store/actions'
import SimulationResults from './SimulationResults'
import ConfirmedLineupIcons from '../common/Table/ConfirmedLineupIcons'
import { IRootState } from '../../store/reducer'
import { prepareLineupProjections } from './helpers/specialPositionsHelper'
import { formatGameTimeET } from './helpers/formatGameTimeET'

const MobileLineup = ({ lineup }: { lineup: Lineup }) => {
  const [selectedPosition, setSelectedPosition] = React.useState('' as string)
  const [isOpen, setIsOpen] = React.useState(false)
  const [openModal, setOpenModal] = React.useState(true);
  const { selectedSlate } = useSelector((state: any) => state.common);
  const sessionStorageKey = `lineupBuilderProjections-${selectedSlate.slateId}`;
  const dispatch = useDispatch();

   const setProj = async () => {
      let currentProjections = await fetch(`${process.env.REACT_APP_API_URL}/api/slatedata/projections?SlateId=${selectedSlate.slateId}`).then(res => res.json())
      currentProjections = prepareLineupProjections(currentProjections ?? [], selectedSlate);
      const sessionProjections = sessionStorage.getItem(sessionStorageKey);
      dispatch(setProjectionsRedux(prepareLineupProjections(
        currentProjections, selectedSlate, sessionProjections ? JSON.parse(sessionProjections) : [],
      )))
      dispatch(setSlateProjections(currentProjections))
    }

  React.useEffect(() => {
    if (lineup.simStatus === 'complete') setOpenModal(true);
  }, [lineup])

  React.useEffect(() => {
    setProj();
  }, [selectedSlate])
  return (
    <>
      <PlayerModal position={selectedPosition} isOpen={isOpen} onExit={() => {
        setIsOpen(false);
        setSelectedPosition('')
      }} />
      {
        lineup.simStatus === 'complete' && (
          <Modal open={openModal} onClose={() => { setOpenModal(false) }} sx={{ fontFamily: 'Inter', color: 'white', backgroundColor: '#0A0A0A' }}>
            <Stack direction='column' justifyContent='space-between' height='100%' width='100%' px='16px' boxSizing='border-box'>
              <SimulationResults />
              <Stack padding='0px' gap={1} pb='24px'>
                <Button fullWidth onClick={() => setOpenModal(false)} sx={{
                  borderRadius: '6px',
                  backgroundColor: '#015ACE',
                  height: '44px',
                  color: 'rgba(255, 255, 255, 1)',
                  padding: '8px 18px',
                  fontSize: '14px',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#015ACE',
                    color: 'rgba(255, 255, 255, .7)',
                  },
                }}>
                  Edit Lineup
                </Button>
                <Button fullWidth onClick={() => { setOpenModal(false); dispatch(setSelectedLineup(null)); }} variant='outlined' sx={{
                  borderRadius: '6px',
                  height: '44px',
                  color: 'rgba(255, 255, 255, 0.87)',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  padding: '8px 18px',
                  fontSize: '14px',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                }}>
                  Back to My Lineups
                </Button>
              </Stack>
            </Stack>
          </Modal>
        )
      }
      {
        lineup?.simStatus === 'loading' && (
          <Stack zIndex={10000} justifyContent='center' alignItems='center' position='absolute' height='100%' width="100%" bgcolor='#1C1C1C' borderRadius='10px'>
            <Stack height='200px' sx={{
              '& img': {
                height: '100%',
                width: '100%',
              }
            }}>
              <img src={`${process.env.PUBLIC_URL}/images/Loadinganimation.gif`} alt="loading" />
            </Stack>
          </Stack>
        )}
      <List sx={{ overflow: 'auto', flex: 1 }}>
        {
          (lineup.simStatus !== 'loading') && (
            lineup.players?.map((player, index) => (
              <ListItem key={index} sx={{ height: 'auto', minHeight: '56px', border: '1px solid rgba(255, 255, 255, 0.05)', padding: '8px 10px' }}>
                <PlayerCard key={index} player={player} onAdd={(position: string) => {
                  setSelectedPosition(position)
                  setIsOpen(true)
                }} />
              </ListItem>
            ))
          )
        }
      </List>
    </>
  )
}

const PlayerCard = ({ player, onAdd }: { player: LineupRubric, onAdd: (position: string) => void }) => {
  const { selectedLineupId } = useSelector((state: any) => state.lineupBuilder);
  const { selectedSlate } = useSelector((state: IRootState) => state.common);
  const dispatch = useDispatch();

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',

    // These options are needed to round to whole numbers if that's what you want.
    minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
  });

  const handleClick = (remove: boolean) => {
    if (remove) {
      dispatch(removePlayer(player, selectedLineupId))
    } else {
      onAdd(player.position)
    }
  };

  const handleToggleLock = () => {
    dispatch(togglePlayerLock(player.nameAndId, selectedLineupId));
  };

  return (
    <Stack direction='row' justifyContent='space-between' columnGap='10px' width='100%' onClick={() => handleClick(false)} padding='10px'>
      <Stack direction='row' justifyContent='center' alignItems='center' color='rgba(255, 255, 255, 0.87)' fontSize='12px' fontWeight={500}>
        {player.position}
      </Stack>
      <Stack direction='row' flexGrow={1} flexShrink={1} justifyContent='space-between' alignItems='center'>
        {
          player.name ? (
            <Stack direction='column' rowGap='4px'>
              <Stack direction='row' alignItems='center'>
                {/* <Stack sx={{
                  '& img': {
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    objectFit: 'contain',
                    marginRight: '4px'
                  }
                }}>
                  <img src='https://awesemostorage.blob.core.windows.net/player-headshots-v2/NBA/120x120-crop/20001796' alt='' />
                </Stack> */}
                <Stack fontSize='12px' fontWeight={500} color='white'>{player.name || 'Derrick Jones Jr.'}</Stack>
                {
                  selectedSlate.sport?.toLowerCase() === 'mlb' && (
                    <Stack paddingLeft='4px'>
                      <ConfirmedLineupIcons confirmed={player.confirmedLineup} battingOrder={player.lineupPosition} />
                    </Stack>
                  )
                }
              </Stack>
              <Stack direction='row' alignItems='center' columnGap='4px'>
                <Stack direction='row' alignItems='center' sx={{
                  '& img': {
                    width: '10px',
                    height: '10px',
                    objectFit: 'contain',
                    marginRight: '4px'
                  }
                }}>
                  <Stack color="rgba(255, 255, 255, 0.70)" fontSize='8px' fontWeight={900}>{player.team}</Stack>
                  {
                    ['mlb', 'nba', 'nhl', 'nfl'].indexOf(selectedSlate?.sport?.toLowerCase()) > -1 && (
                      <img src={player.teamLogo} alt={player.team} />
                    )}
                </Stack>
                <Stack color="rgba(255, 255, 255, 0.70)" fontSize='8px' fontWeight={400}>@</Stack>
                <Stack direction='row' alignItems='center' sx={{
                  '& img': {
                    width: '10px',
                    height: '10px',
                    objectFit: 'contain',
                    marginRight: '4px'
                  }
                }}>
                  <Stack color="rgba(255, 255, 255, 0.70)" fontSize='8px' fontWeight={900}>{player.opponent}</Stack>
                  {
                    ['mlb', 'nba', 'nhl', 'nfl'].indexOf(selectedSlate?.sport?.toLowerCase()) > -1 && (
                      <img src={player.opponentLogo} alt={player.opponent} />
                    )}
                </Stack>
              </Stack>
              <Stack color="rgba(255, 255, 255, 0.70)" fontSize='8px' fontWeight={400}>
                {formatGameTimeET(player.gameTime)}
              </Stack>
            </Stack>
          ) : (
            <Stack color='rgba(255, 255, 255, 0.5)' fontSize='12px' fontWeight={500}>Add a Player</Stack>
          )
        }
        <Stack direction='row'>
          <Stack direction='column' alignItems='flex-end'>
            <Stack color="rgba(255, 255, 255, .7)" fontSize='12px' fontWeight={400}>FPP {player.fpp?.toFixed(2) || '-'}</Stack>
            <Stack color="rgba(255, 255, 255, .7)" fontSize='12px' fontWeight={400}>OWN {(player.ownership * 100)?.toFixed(2) + '%' || '-'}</Stack>
          </Stack>
          <Divider orientation='vertical' flexItem sx={{ mx: '8px', borderColor: 'rgba(255, 255, 255, 0.30)' }} />
        </Stack>
      </Stack>
      <Stack>
        {
          player.name ? (
            <Stack direction='row' columnGap='4px' alignItems='center' justifyContent='space-between' height='100%' width='100%'>
              <Stack fontSize='14px' fontWeight={400} color="rgba(255, 255, 255, .7)">{formatter.format(player.salary)}</Stack>
              <Stack sx={{ height: '24px', width: '24px', cursor: 'pointer' }} onClick={(e) => {
                e.stopPropagation();
                handleToggleLock();
              }}>
                {player.isLocked ? (
                  <LockOutlined style={{ color: '#005CCE', fontSize: '20px' }} />
                ) : (
                  <LockOpenOutlined style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '20px' }} />
                )}
              </Stack>
              <Stack sx={{ height: '24px', width: '24px', cursor: 'pointer' }} onClick={(e) => {
                e.stopPropagation();
                handleClick(true);
              }}>
                <Close />
              </Stack>
            </Stack>
          ) : (
            <Stack direction='row' columnGap='8px' alignItems='center' justifyContent='space-between' height='100%' width='100%'>
              <Stack>-</Stack>
              <Stack sx={{ height: '24px', width: '24px', cursor: 'pointer', color: 'rgba(255, 255, 255, .5)', '& path': { bgcolor: 'red' } }}><AddCircle /></Stack>
            </Stack>
          )
        }
      </Stack>
    </Stack>
  )
}

export default MobileLineup
